import { Link, useRouterState } from '@tanstack/react-router';
import { useVendorAuthStore } from '../store/vendorAuth.ts';
import { Icon } from './Icon.tsx';
import { t } from '@/lib/strings';

type SideItem = {
  to: string;
  label: string;
  icon: string;
  matchPrefix?: string;
};

// Google-Drive-style navigation drawer (desktop), mirroring WapMeet's staff
// Sidebar: full-width destination rows (icon + label) with a tonal pill
// highlight on the active row, no border, sitting under the vendor top app bar
// (top-16). RTL, so it hugs the inline-start (right) edge.
//
// Vendors don't create anything, so there is no elevated "new" button; identity
// and sign-out live in the top app bar, exactly as in the staff shell.
export function VendorSidebar(): JSX.Element | null {
  const vendor = useVendorAuthStore((s) => s.vendor);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  if (!vendor) return null;

  const items: SideItem[] = [
    { to: '/vendor', label: t.vendorPortal.navQuotations, icon: 'description' },
    { to: '/vendor/open', label: t.vendorPortal.navOpen, icon: 'inbox', matchPrefix: '/vendor/open' },
    { to: '/vendor/awards', label: t.vendorPortal.navAwards, icon: 'workspace_premium', matchPrefix: '/vendor/awards' },
  ];

  const isActive = (item: SideItem): boolean =>
    item.matchPrefix ? pathname.startsWith(item.matchPrefix) : pathname === item.to;

  const rowBase = 'flex items-center gap-4 rounded-full px-4 py-2.5 text-[0.95rem] transition-colors';

  return (
    <aside
      aria-label={t.nav.mainNav}
      className="hidden md:flex fixed start-0 top-16 bottom-0 z-30 w-64 flex-col gap-1 px-3 py-4 bg-[hsl(var(--chrome))] overflow-y-auto"
    >
      <nav className="flex flex-col gap-0.5">
        {items.map((item) => {
          const active = isActive(item);
          return (
            <Link
              key={item.to}
              to={item.to}
              aria-current={active ? 'page' : undefined}
              className={[
                rowBase,
                active
                  ? 'bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))] font-medium'
                  : 'text-foreground hover:bg-black/[0.05] dark:hover:bg-white/[0.06]',
              ].join(' ')}
            >
              <Icon name={item.icon} size={22} fill={active} className={active ? '' : 'text-muted-foreground'} />
              <span className="flex-1 truncate">{item.label}</span>
            </Link>
          );
        })}
      </nav>
    </aside>
  );
}
